/**
 * Guest Taste API Service (project100 T1006 — landing gate v2, D-011/D-014)
 * Calls the public raisy_BE guest "taste" endpoint (no auth — the doorway flow):
 * a guest drops a company URL, the engine scans it, ranks investors, and drafts
 * 5 real outreach emails. All progress states come from the backend verbatim;
 * this client never invents statuses.
 *
 * The same module also owns the CLAIM (T1025) — the one call that carries those drafts
 * across the sign-up boundary — because the run it claims is the run this file starts.
 */
import AuthUtils from "@/providers/firebase/authUtils";
import { auth } from "@/config/firebase";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

/**
 * THE SESSION, IF THERE IS ONE — never a requirement (P216, 2026-09-26).
 *
 * The doorway is public, so a stranger must always get through. When someone IS signed in his
 * token rides along, which is what lets the backend count the scan against HIS ceiling rather
 * than his office network's (the PO's rule: 25 unregistered, 50 registered).
 *
 * Read straight from Firebase and NOT via `AuthUtils.ensureAuthToken()`: that helper THROWS and
 * logs a console error when nobody is signed in, and the landing page is where most visitors are
 * nobodies — it would paint a console error for every one of them (and a console error is a QA
 * failure in this house). This one is silent, synchronous-cheap when signed out, and can never
 * fail the scan it is decorating.
 */
async function optionalAuthHeader(): Promise<Record<string, string>> {
  try {
    const user = auth?.currentUser;
    if (!user) return {};
    const token = await user.getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export type TasteState = "scanning" | "ranking" | "drafting" | "done" | "error";

/**
 * THE IDLE LINE'S READ (P100 T1039 · D-031 option B) — how many investors the doorway can rank, and
 * when the pool last changed.
 *
 * A count and a timestamp, nothing else: no identity, no list, nothing personal. It is deliberately
 * the only call in this module that SWALLOWS its own failure, and that is the point of it: the hero
 * may state what the machine holds, and when it cannot, it states NOTHING — an idle line that invents
 * a number, or that leaves a spinner where a fact belongs, is exactly what the persona's implication
 * 13 forbids ("never a machine-idle claim"). Silence is not a failure here; a guess would be.
 */
export interface PoolFacts {
  investors: number;
  /** ISO timestamp of the pool's newest change, or null when the backend cannot say. */
  changed_at: string | null;
}

export async function fetchPoolFacts(): Promise<PoolFacts | null> {
  try {
    const res = await fetch(`${API_URL}/api/v1/guest/pool`, {
      headers: { ...(await optionalAuthHeader()) },
    });
    if (!res.ok) return null;
    const body = await res.json();
    const data = body?.data;
    if (!data || typeof data.investors !== "number") return null;
    return { investors: data.investors, changed_at: data.changed_at ?? null };
  } catch {
    return null;
  }
}

export interface TasteCompany {
  name: string;
  description: string;
  industry: string;
  geography: string;
  raising_stage: string;
  extraction_confidence: number | string;
}

export interface TasteInvestor {
  name: string;
  fit_score: number;
  fit_reasons: string[];
  email_status: string;
}

export interface TasteDraftConfidence {
  email_status: string;
  fit_score: number;
  compliance: Record<string, unknown> | null;
}

export interface TasteDraft {
  investor_name: string;
  subject: string;
  body: string;
  draft_mode: string;
  /** Carried on the draft too, so the payoff can be ONE list: investor + email. */
  fit_score: number;
  fit_reasons: string[];
  confidence: TasteDraftConfidence;
}

/** One step of the backend's own phase rail (T1006 reopen — the wait, shown). */
export interface TasteStep {
  key: string;
  label: string;
  state: 'done' | 'active' | 'pending';
}

/**
 * ONE EVENT PER REAL UNIT OF WORK (P216 T0204 · @P100 D-029).
 *
 * The backend appends one of these the moment a unit FINISHES and never before: the read
 * returning (`company`), the pool being scored (`ranked`), one per email (`draft`, naming the
 * investor). A surface that shows them is showing effort accumulating instead of replacing one
 * line with the next — which is the whole complaint the decision answers.
 *
 * `at` is ELAPSED SECONDS measured by the backend's own clock since the run started, so anything
 * that paces itself off this list is pacing off time that really passed, never a fraction of a
 * plan. The list is additive: a build that ignores it renders exactly as it did before.
 */
export interface TasteEvent {
  at: number;
  /** `company` · `ranked` · `draft` — open, because the backend may add a unit before this build does. */
  kind: string;
  label: string;
  /** `ranked` only: how many were scored, and how many were considered (3 of 240 is the effort). */
  count?: number;
  pool?: number;
  /** `draft` only: which email of how many. */
  index?: number;
  total?: number;
}

export interface TasteBenchmarks {
  reply_rate_pct: string;
  replies_per_200: string;
}

export interface TasteResult {
  company: TasteCompany;
  investors: TasteInvestor[];
  drafts: TasteDraft[];
  benchmarks: TasteBenchmarks;
  /**
   * How many investors were even candidates, so the five he is shown can be read
   * against the pool instead of appearing out of nowhere (T1023).
   */
  pool_size?: number;
}

export interface TasteJobData {
  state: TasteState;
  progress_label: string;
  /**
   * 0-100 from the engine's own stream, or undefined when the backend in front of
   * this build does not report progress yet. The FE NEVER invents a figure: with
   * no value it shows an honest indeterminate bar rather than a frozen 0%.
   */
  progress_percent?: number;
  phase?: string;
  steps?: TasteStep[];
  /**
   * Every unit of work the engine has ALREADY FINISHED, in order (P216 T0204). Absent on a build
   * that does not report it, which is why every reader treats it as optional.
   */
  events?: TasteEvent[];
  drafted?: number;
  draft_total?: number;
  /**
   * The drafts that have LANDED so far (P100 D-028, PO 2026-09-26). The backend
   * publishes each one the moment its writer call returns, so the doorway can
   * render it immediately instead of holding all five until the run ends.
   */
  drafts?: TasteDraft[];
  /** Measured end-to-end time, so the wait can be set as an expectation. */
  typical_seconds?: number;
  result: TasteResult | null;
  error: string | null;
}

interface ApiEnvelope<T> {
  status: boolean;
  statusCode: number;
  message: string;
  data: T;
}

/** Discriminated results — HTTP/network failures are data, never thrown. */
export type TasteStartResult =
  | { ok: true; jobId: string; claimToken: string }
  | {
      ok: false;
      kind: "bad_url" | "rate_limited" | "server" | "network";
      message: string;
      /**
       * Seconds until the doorway opens again — rate_limited only, absent when the backend did
       * not send one. The guard measures its own window; without this the surface can only say
       * "later", and "later" is a dead end for a first-time visitor (P216, 2026-09-26).
       */
      retryAfterSeconds?: number;
      /**
       * WHY it closed, when the backend names it: `capacity` = the day's budget is spent (nobody's
       * fault, and it reopens on its own) rather than this visitor having used his own ceiling.
       * The two are different sentences and the surface must not blur them (P216 D-002).
       */
      reason?: string;
    };

export type TastePollResult =
  | { ok: true; job: TasteJobData }
  | { ok: false; kind: "unknown_job" | "server" | "network"; message: string };

export interface TasteStartOptions {
  stage?: string;
  geography?: string;
}

export async function startTaste(
  url: string,
  opts: TasteStartOptions = {},
): Promise<TasteStartResult> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}/api/v1/guest/taste`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await optionalAuthHeader()) },
      body: JSON.stringify({
        url,
        ...(opts.stage ? { stage: opts.stage } : {}),
        ...(opts.geography ? { geography: opts.geography } : {}),
      }),
    });
  } catch {
    return {
      ok: false,
      kind: "network",
      message: "Could not reach the engine. Check your connection and try again.",
    };
  }

  const body = (await res.json().catch(() => null)) as
    | ApiEnvelope<{
        job_id?: string;
        /** P100: the canonical name for the same handle (manifest MR0p: the handle IS the execution id).
         *  Preferred where present; `job_id` is the pre-rename alias and goes with the next pass. */
        execution_id?: string;
        claim_token?: string;
        retry_after_seconds?: number;
        reason?: string;
      }>
    | null;

  if (res.status === 429) {
    return {
      ok: false,
      kind: "rate_limited",
      message: body?.message || "Five scans an hour run per network, and this one has used all five.",
      // WHEN IT OPENS. The backend measures its own window and sends the seconds; without this
      // the surface can only say "later", which is a dead end for a first-time visitor.
      retryAfterSeconds: body?.data?.retry_after_seconds,
      reason: body?.data?.reason,
    };
  }
  if (res.status === 400) {
    return {
      ok: false,
      kind: "bad_url",
      message: body?.message || "That URL does not look reachable. Check the address and try again.",
    };
  }
  /* The handle, under whichever name the backend sent it. `?? ""` rather than relying on the guard's
     narrowing: the type is then `string` by construction, so the alias costs the type checker nothing. */
  const executionId: string = body?.data?.execution_id ?? body?.data?.job_id ?? "";
  if (!res.ok || !body?.status || !executionId) {
    return {
      ok: false,
      kind: "server",
      message: body?.message || "The engine could not start the scan. Please try again.",
    };
  }
  return { ok: true, jobId: executionId, claimToken: body?.data?.claim_token ?? "" };
}

/**
 * THE SAVED RUN — one definition, two readers (P100 T1025).
 *
 * The doorway saves the job id so a signup round-trip does not lose the run; the account
 * surface reads the SAME key to claim it. It lives here, beside the API that owns the job,
 * so the two surfaces cannot drift on the key or on the shape of what is stored.
 */
export const TASTE_JOB_KEY = "raisy_taste_job";

export interface SavedTasteJob {
  jobId: string;
  /**
   * The secret that proves this run is HIS when he comes back signed in (T1025). It is an
   * opaque token, never the job id: the engine's own identifier must not be what proves
   * ownership of a run.
   */
  claimToken: string;
  startedAt: number;
  url: string;
  stage: string;
}

/** The run this browser is holding, or null. Never throws (private mode included). */
export function loadSavedTasteJob(): SavedTasteJob | null {
  try {
    const raw = sessionStorage.getItem(TASTE_JOB_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedTasteJob;
    return parsed?.jobId ? parsed : null;
  } catch {
    return null;
  }
}

/** Only the claim removes it — until the five are in the account, the run is still owed. */
export function clearSavedTasteJob(): void {
  try {
    sessionStorage.removeItem(TASTE_JOB_KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * T1025 — THE CLAIM: turn the run the doorway showed into the account's own drafts.
 *
 * This is the click that keeps the promise. Absent an entity, the backend builds the
 * company the doorway read (through the path the wizard uses, duplicate guard intact) and
 * returns its id, so the caller lands on the company the five belong to rather than having
 * to find it.
 *
 * Every failure is DATA, never a thrown error, because each one is a different sentence on
 * the surface: a scan that cannot be found is re-runnable, a company that already exists is
 * openable, and a server fault is worth retrying.
 */
export type TasteClaimResult =
  | {
      ok: true;
      entityId: string;
      drafted: number;
      already: number;
      total: number;
      /** HIS OWN name for the company the five landed in — he may have edited the prefill. */
      companyName: string;
      /**
       * The claim BUILT this company (P218 D-218-007). A company the claim builds is a BARE one:
       * name, description, industry and location, and nothing else — so the surface says what
       * exists and what does not, instead of leaving him to guess whether it is finished.
       */
      companyCreated: boolean;
    }
  | {
      /**
       * THE COLLISION WITH NO COMPANY OF HIS OWN (P218 D-218-005) — a STATE, not a failure.
       *
       * `ok: false` because the five are not in his account YET; the claim itself did nothing
       * wrong. He confirms ONE name — prefilled from his own scan, so nothing is re-typed (the
       * persona fails on a blank field) — and they land in a company that is his.
       */
      ok: false;
      kind: "needs_company";
      companyName: string;
      companyWebsite: string;
      message: string;
    }
  | {
      ok: false;
      kind: "unauthenticated" | "duplicate" | "not_found" | "forbidden" | "server" | "network";
      message: string;
      /** On `duplicate`: the company that already exists, so it can be opened by name. */
      existingEntityId?: string;
    };

export async function claimTaste(
  claimToken: string,
  entityId?: string | null,
  /**
   * THE COMPANY HE IS LOOKING AT — sent so the BE never has to guess where the five belong when the
   * scanned company turns out to be one he cannot have (PO 2026-09-27: no fallbacks, production
   * grade only; the alternative was the BE picking "the newest company he can write to").
   *
   * IT MUST BE A COMPANY HE MAY WRITE TO. Marking that here because the caller is the only party
   * that can know it: `/user/init` gives every account a `person` entity it holds `editor` on, and
   * sending that would ask the backend to file five investor emails in his own record — which is
   * what it did on 2026-09-28 (P218 T218-012).
   */
  preferredEntityId?: string | null,
  /**
   * THE NAME HE CONFIRMED (P218 D-218-005) — sent ONLY on the retry, after the collision state
   * asked him for one. It is the difference between a company the machine invented for him (which
   * this product refuses to do) and one he authored himself.
   */
  companyName?: string | null,
): Promise<TasteClaimResult> {
  const token = await AuthUtils.ensureAuthToken().catch(() => null);
  if (!token) {
    return { ok: false, kind: "unauthenticated", message: "Sign in to bring your five across." };
  }

  let res: Response;
  try {
    res = await fetch(`${API_URL}/api/v1/guest/taste/claim`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        claim_token: claimToken,
        ...(entityId ? { entity_id: entityId } : {}),
        ...(preferredEntityId ? { preferred_entity_id: preferredEntityId } : {}),
        ...(companyName ? { company_name: companyName } : {}),
      }),
    });
  } catch {
    return {
      ok: false,
      kind: "network",
      message: "Could not reach the engine to move your drafts. Try again.",
    };
  }

  const body = (await res.json().catch(() => null)) as
    | ApiEnvelope<{
        entity_id?: string;
        drafted?: number;
        already?: number;
        total?: number;
        company_name?: string;
        company_created?: boolean;
        needs_your_company?: boolean;
        company_website?: string;
      }>
    | null;

  if (res.ok && body?.status && body.data?.entity_id) {
    return {
      ok: true,
      entityId: body.data.entity_id,
      drafted: body.data.drafted ?? 0,
      already: body.data.already ?? 0,
      total: body.data.total ?? 0,
      companyName: body.data.company_name ?? "",
      companyCreated: body.data.company_created === true,
    };
  }

  // THE COLLISION STATE (D-218-005) rides on HTTP 200 with `status: true`: a state he must act on
  // is not a transport error, and the 409 it replaces is exactly what read as a broken promise.
  if (res.ok && body?.status && body.data?.needs_your_company) {
    return {
      ok: false,
      kind: "needs_company",
      companyName: body.data.company_name ?? "",
      companyWebsite: body.data.company_website ?? "",
      message: body.message || "Your five are ready. They need a company of yours to live in.",
    };
  }

  const detail = (body?.data ?? null) as { existing_entity_id?: string } | null;
  const kind: "duplicate" | "not_found" | "forbidden" | "server" =
    res.status === 409
      ? "duplicate"
      : res.status === 404
        ? "not_found"
        : res.status === 403
          ? "forbidden"
          : "server";

  return {
    ok: false,
    kind,
    message: body?.message || "Those drafts could not be moved yet.",
    existingEntityId: detail?.existing_entity_id,
  };
}

export async function fetchTaste(jobId: string): Promise<TastePollResult> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}/api/v1/guest/taste/${encodeURIComponent(jobId)}`);
  } catch {
    return { ok: false, kind: "network", message: "Connection hiccup." };
  }

  const body = (await res.json().catch(() => null)) as ApiEnvelope<TasteJobData | null> | null;

  if (res.status === 404) {
    return {
      ok: false,
      kind: "unknown_job",
      message: "This scan session has expired. Run it again and it starts fresh.",
    };
  }
  if (!res.ok || !body?.status || !body.data) {
    return {
      ok: false,
      kind: "server",
      message: body?.message || "The engine returned an unexpected answer.",
    };
  }
  return { ok: true, job: body.data };
}
